'use client';

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  ChevronRightIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Button from '~/components/Button';
import Dropdown from '~/components/Dropdown';
import TruncatedText from '~/components/TruncatedText';
import { Checkbox } from '~/components/ui/checkbox';
import usePopover from '~/hooks/usePopover';
import { cn } from '~/lib/utils';

const MENU_CLASS = 'filter-menu';

export type FilterMenuOption = {
  key: string;
  label: ReactNode;
  searchText?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

export type FilterMenuCategory = {
  key: string;
  label: string;
  icon?: ReactNode;
  count?: number;
  options: FilterMenuOption[];
};

export type FilterMenuProps = {
  categories: FilterMenuCategory[];
  /** Override the active count. Defaults to the sum of category counts. */
  activeCount?: number;
  /**
   * Active-dot on the Filters trigger and the adjacent clear control.
   * Hide when selected values are shown as pills elsewhere.
   */
  showCount?: boolean;
  /** Shown at the bottom of a single-category menu when filters are active. */
  onClear?: () => void;
  className?: string;
};

function optionSearchText(option: FilterMenuOption) {
  if (option.searchText) return option.searchText;
  return typeof option.label === 'string' ? option.label : '';
}

function matchesQuery(option: FilterMenuOption, query: string) {
  if (!query.trim()) return true;
  return optionSearchText(option).toLowerCase().includes(query.trim().toLowerCase());
}

function OptionList({ options, query }: { options: FilterMenuOption[]; query: string }) {
  const visible = options.filter((option) => matchesQuery(option, query));
  if (!visible.length) {
    return <div className="text-muted-foreground px-2.5 py-2 text-sm">No matches</div>;
  }

  return (
    <>
      {visible.map((option) => (
        <button
          key={option.key}
          type="button"
          className="hover:bg-accent flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left"
          onClick={() => option.onCheckedChange(!option.checked)}
        >
          <Checkbox checked={option.checked} tabIndex={-1} className="pointer-events-none" />
          <span className="text-foreground min-w-0 flex-1 truncate text-sm">{option.label}</span>
        </button>
      ))}
    </>
  );
}

/** Funnel icon with an optional active dot that does not change trigger width. */
export function FilterTriggerIcon({ active = false }: { active?: boolean }) {
  return (
    <span className="relative inline-flex">
      <FunnelIcon className="size-3.5" strokeWidth={2} />
      {active ? (
        <span
          aria-hidden
          className="bg-foreground absolute -top-px -right-px size-1.5 rounded-full"
        />
      ) : null}
    </span>
  );
}

function SearchField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="relative px-1.5 pt-1 pb-1">
      <MagnifyingGlassIcon
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 size-3.5 -translate-y-1/2"
        strokeWidth={2}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Filter…"
        className="border-border bg-background placeholder:text-muted-foreground focus-visible:ring-ring/30 h-8 w-full rounded-md border pr-2.5 pl-8 text-sm outline-none focus-visible:ring-2"
        autoFocus
      />
    </div>
  );
}

/**
 * Shared Filters control. One category opens a simple list; two or more open a
 * category menu with the value panel to the left of the hovered row.
 */
export default function FilterMenu({
  categories: rawCategories,
  activeCount: activeCountProp,
  showCount = true,
  onClear,
  className,
}: FilterMenuProps) {
  const categories = rawCategories.filter((category) => category.options.length > 0);
  const isMulti = categories.length > 1;
  const { open, handleOpen, handleClose, anchorEl } = usePopover<HTMLButtonElement>();
  const categoryRef = useRef<HTMLDivElement>(null);
  const [submenu, setSubmenu] = useState<string | null>(null);
  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [submenuTop, setSubmenuTop] = useState(4);
  const [query, setQuery] = useState('');

  const activeCount =
    activeCountProp ?? categories.reduce((sum, category) => sum + (category.count ?? 0), 0);

  const closeMenu = () => {
    setSubmenu(null);
    setSubmenuAnchor(null);
    setQuery('');
    handleClose();
  };

  const openSubmenu = (key: string, el: HTMLElement) => {
    if (submenu !== key) setQuery('');
    setSubmenu(key);
    setSubmenuAnchor(el);
  };

  useLayoutEffect(() => {
    if (!submenuAnchor || !categoryRef.current) return;
    setSubmenuTop(
      submenuAnchor.getBoundingClientRect().top - categoryRef.current.getBoundingClientRect().top,
    );
  }, [submenuAnchor, submenu]);

  const activeCategory = categories.find((category) => category.key === submenu);
  const showSearch = (activeCategory?.options.length ?? 0) > 3;
  const singleCategory = categories[0];
  const showSingleSearch = !isMulti && (singleCategory?.options.length ?? 0) > 3;

  return (
    <div data-filter-menu="" className={cn('flex shrink-0 items-center gap-1.5', className)}>
      <Button
        type="button"
        size="sm"
        variant="outline"
        aria-label={activeCount > 0 ? `Filters, ${activeCount} selected` : 'Filters'}
        icon={<FilterTriggerIcon active={showCount && activeCount > 0} />}
        label="Filters"
        onClick={(e) => {
          if (open) {
            closeMenu();
            return;
          }
          setSubmenu(null);
          setSubmenuAnchor(null);
          setQuery('');
          handleOpen(e);
        }}
      />
      {showCount && onClear && activeCount > 0 && isMulti ? (
        <button
          type="button"
          aria-label="Clear filters"
          className="text-muted-foreground hover:text-foreground hover:bg-accent inline-flex size-7 items-center justify-center rounded-md"
          onClick={onClear}
        >
          <XMarkIcon className="size-3.5" strokeWidth={2} />
        </button>
      ) : null}

      {isMulti ? (
        <Dropdown
          open={open}
          anchorEl={anchorEl}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disablePortal={false}
          disableEnforceFocus
          disableAutoFocus
          disableRestoreFocus
          slotProps={{
            paper: { className: `${MENU_CLASS} mt-1.5 max-w-none overflow-visible rounded-xl` },
          }}
        >
          <div className="relative">
            <div ref={categoryRef} className="w-[220px] p-1">
              <div className="space-y-0.5 py-0.5">
                {categories.map((item) => {
                  const isActive = submenu === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={cn(
                        'flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left transition-colors',
                        isActive ? 'bg-accent' : 'hover:bg-accent',
                      )}
                      onMouseEnter={(e) => openSubmenu(item.key, e.currentTarget)}
                      onFocus={(e) => openSubmenu(item.key, e.currentTarget)}
                      onClick={(e) => {
                        e.stopPropagation();
                        openSubmenu(item.key, e.currentTarget);
                      }}
                    >
                      {item.icon ? (
                        <span className="text-muted-foreground">{item.icon}</span>
                      ) : null}
                      <span className="text-foreground min-w-0 flex-1 text-sm font-medium">
                        {item.label}
                      </span>
                      {(item.count ?? 0) > 0 && (
                        <span className="bg-muted text-muted-foreground rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums">
                          {item.count}
                        </span>
                      )}
                      <ChevronRightIcon
                        className="text-muted-foreground size-3.5"
                        strokeWidth={2}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            {activeCategory ? (
              <div
                className="border-border bg-popover absolute top-0 right-full mr-1.5 w-[260px] rounded-xl border p-1 shadow-lg"
                style={{ top: submenuTop }}
              >
                {showSearch ? <SearchField value={query} onChange={setQuery} /> : null}
                <div className="max-h-72 space-y-0.5 overflow-y-auto py-0.5">
                  <OptionList options={activeCategory.options} query={query} />
                </div>
              </div>
            ) : null}
          </div>
        </Dropdown>
      ) : singleCategory ? (
        <Dropdown
          open={open}
          anchorEl={anchorEl}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disablePortal={false}
          slotProps={{ paper: { className: `${MENU_CLASS} mt-1.5 rounded-xl` } }}
        >
          <div className="w-[220px] p-1">
            <div className="text-muted-foreground px-2.5 py-1.5 text-[11px] font-medium tracking-[0.08em] uppercase">
              {singleCategory.label}
            </div>
            {showSingleSearch ? <SearchField value={query} onChange={setQuery} /> : null}
            <div className="max-h-64 space-y-0.5 overflow-y-auto py-0.5">
              <OptionList options={singleCategory.options} query={query} />
            </div>
            {onClear && activeCount > 0 ? (
              <div className="border-border/60 mt-0.5 border-t pt-0.5">
                <button
                  type="button"
                  className="text-muted-foreground hover:bg-accent hover:text-foreground w-full rounded-md px-2.5 py-1.5 text-left text-sm transition-colors"
                  onClick={() => {
                    onClear();
                    closeMenu();
                  }}
                >
                  Clear filters
                </button>
              </div>
            ) : null}
          </div>
        </Dropdown>
      ) : null}
    </div>
  );
}

export function FilterPill({
  icon,
  field,
  operator = 'is',
  value,
  onRemove,
}: {
  icon: ReactNode;
  field: string;
  operator?: string;
  value: string;
  onRemove: () => void;
}) {
  return (
    <span className="border-border/80 bg-background inline-flex h-7 max-w-full items-center overflow-hidden rounded-md border text-xs shadow-[0_1px_0_rgba(0,0,0,0.02)]">
      <span className="text-muted-foreground inline-flex items-center gap-1.5 border-r border-border/70 px-2 py-1">
        <span className="opacity-70">{icon}</span>
        <span className="font-medium">{field}</span>
      </span>
      <span className="text-muted-foreground/80 px-1.5">{operator}</span>
      <TruncatedText className="text-foreground max-w-40 px-0.5 font-medium">{value}</TruncatedText>
      <button
        type="button"
        aria-label={`Remove ${field} filter`}
        onClick={onRemove}
        className="text-muted-foreground hover:bg-accent hover:text-foreground ml-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-sm transition-colors"
      >
        <XMarkIcon className="size-3" strokeWidth={2.5} />
      </button>
    </span>
  );
}
